import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
} from '@angular/core';
import { TranslocoService, TranslocoDirective } from '@jsverse/transloco';
import { CurrencyAmountDisplay } from 'src/app/core/services/currency/currency-format';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { GameInterfaceState } from '../../game/controllers/listeners-legacy/ux-game-listener';
import { GameId } from 'src/app/model/game/game-id';
import { GameBehaviorType } from '../../game/controllers/behaviors/game-behavior';
import { AlertController } from '@ionic/angular/standalone';
import { AlertManagerService } from 'src/app/core/services/alert-stack-manager/alert-stack-manager.service';
import { ThemePipe } from '../../../core/pipes/theme.pipe';
import { TimeFormatGhostPipe } from '../../../core/pipes/time-format-ghost.pipe';
import { TimeFormatPipe } from '../../../core/pipes/time-format.pipe';
import { IconComponent } from '../../../ui/icon/icon.component';
import { TournamentPillComponent } from '../../../ui/tournament/tournament-pill/tournament-pill.component';
import { CurrencyIconComponent } from '../../../ui/currency-icon/currency-icon.component';
import { GameTitleComponent } from '../../../ui/game-title/game-title.component';
import { NavButtonComponent } from '../../../ui/buttons/nav-button/nav-button.component';
import { NgStyle, NgClass } from '@angular/common';

@Component({
  selector: 'app-game-score',
  templateUrl: './game-score.component.html',
  styleUrls: ['./game-score.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoDirective,
    NavButtonComponent,
    GameTitleComponent,
    NgStyle,
    CurrencyIconComponent,
    TournamentPillComponent,
    IconComponent,
    NgClass,
    TimeFormatPipe,
    TimeFormatGhostPipe,
    ThemePipe,
  ],
})
export class GameScoreComponent {
  @Input() gameName?: string;
  @Input() gameBehaviorType?: GameBehaviorType;
  @Input({ required: true }) gameId!: GameId;
  @Input({ required: true }) gameInterfaceState!: GameInterfaceState;
  @Input() score?: string;
  @Input() reward?: { primary: CurrencyAmountDisplay; secondary: CurrencyAmountDisplay };
  @Input() countdown?: number;
  @Input() tournamentId?: string;

  @Output() backClicked = new EventEmitter<void>();
  @Output() closeClicked = new EventEmitter<void>();
  @Output() cheatSheetClicked = new EventEmitter<void>();

  private alertController = inject(AlertController);
  private alertManagerService = inject(AlertManagerService);
  themeService = inject(ThemeService);
  translocoService = inject(TranslocoService);

  GameInterfaceState = GameInterfaceState;

  readonly gameNameStyles: [string, string][] = [
    ['text-color', 'game-header-title'],
    ['type', 'game-header-title'],
  ];

  readonly eventTimerStyles: [string, string][] = [
    ['text-color', 'game-header-event-timer'],
    ['type', 'game-header-event-timer'],
  ];

  readonly eventCountdownStyles: [string, string][] = [
    ['text-color', 'game-header-event-countdown'],
    ['type', 'game-header-event-countdown'],
  ];

  readonly prizeStyles: [string, string][] = [
    ['text-color', 'game-header-prize'],
    ['type', 'game-header-prize'],
  ];

  readonly scorePillStyles: [string, string][] = [
    ['background', 'pill-score'],
    ['border-radius', 'pill-score'],
    ['text-color', 'pill-score-label'],
    ['type', 'pill-score-label'],
  ];

  readonly timerPillStyles: [string, string][] = [
    ['background', 'pill-score'],
    ['border-radius', 'pill-score'],
    ['text-color', 'pill-score-label'],
    ['type', 'pill-score-label'],
    ['border', 'pill-countdown-danger'],
  ];

  readonly currencyPillSubLabelStyles: [string, string][] = [
    ['text-color', 'pill-currency-sub-label'],
    ['type', 'pill-currency-sub-label'],
  ];

  readonly scorePillSubLabelStyles: [string, string][] = [
    ['text-color', 'pill-currency-sub-label'],
    ['type', 'pill-currency-sub-label'],
  ];

  isEnded(): boolean {
    return this.countdown !== undefined && this.countdown <= 0;
  }

  onBackClicked() {
    this.backClicked.emit();
  }

  async onCloseClicked() {
    if (!this.themeService.hasGameCloseWarning()) {
      this.closeClicked.emit();
      return;
    }

    const alert = await this.alertController.create({
      header: this.translocoService.translate('pages.game-component.close-game-alert-header'),
      message: this.translocoService.translate('pages.game-component.close-game-alert-message'),
      cssClass: 'alert',
      buttons: [
        {
          text: this.translocoService.translate('pages.game-component.close-game-alert-cancel'),
          cssClass: 'alert-button',
          handler: () => {
            alert.dismiss();
          },
        },
        {
          text: this.translocoService.translate('pages.game-component.close-game-alert-close'),
          cssClass: 'alert-button',
          handler: () => {
            this.closeClicked.emit();
          },
        },
      ],
      backdropDismiss: false,
    });

    this.alertManagerService.push(alert);
    await alert.present();
  }

  onCheatSheetClicked() {
    this.cheatSheetClicked.emit();
  }

  get hasSecondaryCurrency(): boolean {
    return this.themeService.hasSecondaryCurrency;
  }

  get showScore(): boolean {
    return !!this.score && this.gameId !== GameId.Marbles; // Score is hidden in Marbles, this is decision by design.
  }

  get primaryRewardLabel(): string {
    if (this.reward?.primary.text) {
      return this.reward.primary.text;
    }

    return this.gameBehaviorType === GameBehaviorType.Local
      ? this.translocoService.translate('pages.game-component.local')
      : this.translocoService.translate('pages.game-component.practice');
  }

  get tournamentLabel(): string {
    return this.translocoService.translate('components.tournament.tournament');
  }
}
